import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Shield,
  ArrowLeft,
  FileText,
  Plus,
  Loader2,
  Calendar,
  Building2,
  Upload,
  Trash2,
  Download,
  RefreshCw,
  AlertTriangle,
  User,
  Phone,
  Mail,
  Briefcase,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DOC_TYPES = [
  { value: 'policy', label: 'Insurance Policy' },
  { value: 'certificate', label: 'Certificate of Insurance' },
  { value: 'schedule', label: 'Insurance Schedule' },
  { value: 'claim_form', label: 'Claim Form Template' },
  { value: 'broker_agreement', label: 'Broker Agreement' },
  { value: 'quote', label: 'Insurance Quote' },
  { value: 'invoice', label: 'Premium Invoice' },
  { value: 'other', label: 'Other' },
];

export default function InsuranceVault() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { hoaId } = useParams();
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [broker, setBroker] = useState(null);
  const [activeTab, setActiveTab] = useState('documents');
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showBrokerDialog, setShowBrokerDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [uploadForm, setUploadForm] = useState({
    name: '',
    document_type: '',
    description: '',
    expiry_date: '',
    file: null
  });

  const [brokerForm, setBrokerForm] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    address: '',
    policy_number: '',
    insurer_name: '',
    policy_expiry: '',
    notify_on_claim: true
  });

  const token = localStorage.getItem('session_token');
  const buildingId = hoaId || user?.hoa_id || localStorage.getItem('hoa_id');
  const agentId = localStorage.getItem('agent_id') || 'mac_demo_001';

  useEffect(() => {
    if (buildingId) {
      fetchData();
    }
  }, [buildingId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      // Get vault documents
      const docsRes = await fetch(`${API_URL}/api/insurance/vault/${buildingId}`, { headers });
      if (docsRes.ok) {
        setDocuments(await docsRes.json());
      }
      
      // Get broker info
      const brokerRes = await fetch(`${API_URL}/api/insurance/broker/${agentId}`, { headers });
      if (brokerRes.ok) {
        const data = await brokerRes.json();
        if (data.exists) {
          setBroker(data.broker);
          setBrokerForm({
            company_name: data.broker.company_name || '',
            contact_name: data.broker.contact_name || '',
            email: data.broker.email || '',
            phone: data.broker.phone || '',
            address: data.broker.address || '',
            policy_number: data.broker.policy_number || '',
            insurer_name: data.broker.insurer_name || '',
            policy_expiry: data.broker.policy_expiry || '',
            notify_on_claim: data.broker.notify_on_claim ?? true
          });
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadDocument = async () => {
    if (!uploadForm.name || !uploadForm.document_type || !uploadForm.file) {
      toast.error('Please fill in required fields and select a file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', uploadForm.file);
    formData.append('name', uploadForm.name);
    formData.append('document_type', uploadForm.document_type);
    if (uploadForm.description) formData.append('description', uploadForm.description);
    if (uploadForm.expiry_date) formData.append('expiry_date', uploadForm.expiry_date);

    try {
      const res = await fetch(
        `${API_URL}/api/insurance/vault/${buildingId}/upload?name=${encodeURIComponent(uploadForm.name)}&document_type=${uploadForm.document_type}${uploadForm.description ? '&description=' + encodeURIComponent(uploadForm.description) : ''}${uploadForm.expiry_date ? '&expiry_date=' + uploadForm.expiry_date : ''}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        }
      );

      if (res.ok) {
        toast.success('Document uploaded');
        setShowUploadDialog(false);
        setUploadForm({ name: '', document_type: '', description: '', expiry_date: '', file: null });
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Upload failed');
      }
    } catch (error) {
      toast.error(error?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!confirm('Delete this document?')) return;

    try {
      const res = await fetch(`${API_URL}/api/insurance/vault/${docId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Document deleted');
        fetchData();
      }
    } catch (error) {
      toast.error(error?.message || 'Delete failed');
    }
  };

  const handleSaveBroker = async () => {
    if (!brokerForm.company_name || !brokerForm.email) {
      toast.error('Please fill in company name and email');
      return;
    }

    try {
      let res;
      if (broker) {
        // Update existing
        res = await fetch(`${API_URL}/api/insurance/broker/${broker.broker_id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(brokerForm)
        });
      } else {
        // Create new
        res = await fetch(`${API_URL}/api/insurance/broker/create?agent_id=${agentId}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(brokerForm)
        });
      }

      if (res.ok) {
        toast.success(broker ? 'Broker updated' : 'Broker added');
        setShowBrokerDialog(false);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save broker');
      }
    } catch (error) {
      toast.error(error?.message || 'Failed to save broker');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-ZA', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getDocTypeName = (type) => {
    const dt = DOC_TYPES.find(d => d.value === type);
    return dt?.label || type;
  };

  const isExpiringSoon = (dateStr) => {
    if (!dateStr) return false;
    const expiry = new Date(dateStr);
    const thirtyDays = new Date();
    thirtyDays.setDate(thirtyDays.getDate() + 30);
    return expiry <= thirtyDays;
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 lg:p-8" data-testid="insurance-vault">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button variant="ghost" className="mb-2 -ml-2" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 flex items-center gap-3">
                <Shield className="w-8 h-8 text-indigo-600" />
                Insurance Vault
              </h1>
              <p className="text-slate-500 mt-1">Store and manage insurance documents</p>
            </div>
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="documents">Documents ({documents.length})</TabsTrigger>
            <TabsTrigger value="broker">Insurance Broker</TabsTrigger>
          </TabsList>

          {/* Documents Tab */}
          <TabsContent value="documents">
            <div className="flex justify-end mb-4">
              <Button onClick={() => setShowUploadDialog(true)}>
                <Upload className="w-4 h-4 mr-2" />
                Upload Document
              </Button>
            </div>

            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
              </div>
            ) : documents.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Documents</h3>
                  <p className="text-slate-500 mb-4">Upload insurance documents to keep them organized.</p>
                  <Button onClick={() => setShowUploadDialog(true)}>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Document
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {documents.map(doc => (
                  <Card key={doc.document_id}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                            <FileText className="w-5 h-5 text-indigo-600" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold">{doc.name}</h3>
                              <Badge variant="outline">{getDocTypeName(doc.document_type)}</Badge>
                              {doc.expiry_date && isExpiringSoon(doc.expiry_date) && (
                                <Badge className="bg-amber-100 text-amber-700">
                                  <AlertTriangle className="w-3 h-3 mr-1" />
                                  Expiring Soon
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-4 text-xs text-slate-500 mt-1">
                              {doc.description && <span>{doc.description}</span>}
                              <span>{formatFileSize(doc.file_size)}</span>
                              {doc.expiry_date && (
                                <span className={isExpiringSoon(doc.expiry_date) ? 'text-amber-600' : ''}>
                                  Expires: {formatDate(doc.expiry_date)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" asChild>
                            <a href={`${API_URL}${doc.file_url}?token=${token}`} target="_blank" rel="noopener noreferrer">
                              <Download className="w-4 h-4" />
                            </a>
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDeleteDocument(doc.document_id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Broker Tab */}
          <TabsContent value="broker">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
              </div>
            ) : broker ? (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <Briefcase className="w-5 h-5" />
                      {broker.company_name}
                    </CardTitle>
                    <Button variant="outline" onClick={() => setShowBrokerDialog(true)}>
                      Edit
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    {broker.contact_name && (
                      <div>
                        <p className="text-sm text-slate-500">Contact Person</p>
                        <p className="font-medium flex items-center gap-2">
                          <User className="w-4 h-4" /> {broker.contact_name}
                        </p>
                      </div>
                    )}
                    <div>
                      <p className="text-sm text-slate-500">Email</p>
                      <p className="font-medium flex items-center gap-2">
                        <Mail className="w-4 h-4" /> {broker.email}
                      </p>
                    </div>
                    {broker.phone && (
                      <div>
                        <p className="text-sm text-slate-500">Phone</p>
                        <p className="font-medium flex items-center gap-2">
                          <Phone className="w-4 h-4" /> {broker.phone}
                        </p>
                      </div>
                    )}
                    {broker.insurer_name && (
                      <div>
                        <p className="text-sm text-slate-500">Insurance Company</p>
                        <p className="font-medium">{broker.insurer_name}</p>
                      </div>
                    )}
                    {broker.policy_number && (
                      <div>
                        <p className="text-sm text-slate-500">Policy Number</p>
                        <p className="font-medium">{broker.policy_number}</p>
                      </div>
                    )}
                    {broker.policy_expiry && (
                      <div>
                        <p className="text-sm text-slate-500">Policy Expiry</p>
                        <p className={`font-medium ${isExpiringSoon(broker.policy_expiry) ? 'text-amber-600' : ''}`}>
                          {formatDate(broker.policy_expiry)}
                          {isExpiringSoon(broker.policy_expiry) && (
                            <AlertTriangle className="w-4 h-4 inline ml-2" />
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="pt-2 border-t">
                    <Badge className={broker.notify_on_claim ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>
                      {broker.notify_on_claim ? 'Auto-notified on new claims' : 'Not auto-notified'}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-12 text-center">
                  <Briefcase className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Broker Configured</h3>
                  <p className="text-slate-500 mb-4">Add your insurance broker details for automatic notifications.</p>
                  <Button onClick={() => setShowBrokerDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Broker
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Upload Document Dialog */}
        <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Upload Document</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Document Name *</Label>
                <Input
                  value={uploadForm.name}
                  onChange={(e) => setUploadForm({...uploadForm, name: e.target.value})}
                  placeholder="e.g., 2026 Insurance Policy"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Document Type *</Label>
                <Select
                  value={uploadForm.document_type}
                  onValueChange={(v) => setUploadForm({...uploadForm, document_type: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {DOC_TYPES.map(dt => (
                      <SelectItem key={dt.value} value={dt.value}>{dt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Description</Label>
                <Input
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({...uploadForm, description: e.target.value})}
                  placeholder="Brief description..."
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Expiry Date</Label>
                <Input
                  type="date"
                  value={uploadForm.expiry_date}
                  onChange={(e) => setUploadForm({...uploadForm, expiry_date: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>File *</Label>
                <Input
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                  onChange={(e) => setUploadForm({...uploadForm, file: e.target.files[0]})}
                  className="mt-1"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUploadDialog(false)}>Cancel</Button>
              <Button onClick={handleUploadDocument} disabled={uploading}>
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Upload'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Broker Dialog */}
        <Dialog open={showBrokerDialog} onOpenChange={setShowBrokerDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{broker ? 'Edit Broker' : 'Add Insurance Broker'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              <div>
                <Label>Broker Company Name *</Label>
                <Input
                  value={brokerForm.company_name}
                  onChange={(e) => setBrokerForm({...brokerForm, company_name: e.target.value})}
                  placeholder="ABC Insurance Brokers"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Contact Person</Label>
                <Input
                  value={brokerForm.contact_name}
                  onChange={(e) => setBrokerForm({...brokerForm, contact_name: e.target.value})}
                  placeholder="John Smith"
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={brokerForm.email}
                    onChange={(e) => setBrokerForm({...brokerForm, email: e.target.value})}
                    placeholder="broker@email.com"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={brokerForm.phone}
                    onChange={(e) => setBrokerForm({...brokerForm, phone: e.target.value})}
                    placeholder="+27 21 555 1234"
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label>Address</Label>
                <Input
                  value={brokerForm.address}
                  onChange={(e) => setBrokerForm({...brokerForm, address: e.target.value})}
                  placeholder="123 Insurance Street, Cape Town"
                  className="mt-1"
                />
              </div>
              <hr />
              <div>
                <Label>Insurance Company</Label>
                <Input
                  value={brokerForm.insurer_name}
                  onChange={(e) => setBrokerForm({...brokerForm, insurer_name: e.target.value})}
                  placeholder="e.g., Old Mutual, Santam"
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Policy Number</Label>
                  <Input
                    value={brokerForm.policy_number}
                    onChange={(e) => setBrokerForm({...brokerForm, policy_number: e.target.value})}
                    placeholder="POL-123456"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Policy Expiry</Label>
                  <Input
                    type="date"
                    value={brokerForm.policy_expiry}
                    onChange={(e) => setBrokerForm({...brokerForm, policy_expiry: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="notify_on_claim"
                  checked={brokerForm.notify_on_claim}
                  onChange={(e) => setBrokerForm({...brokerForm, notify_on_claim: e.target.checked})}
                />
                <Label htmlFor="notify_on_claim" className="cursor-pointer">
                  Automatically notify broker when new claims are submitted
                </Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowBrokerDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveBroker}>
                {broker ? 'Save Changes' : 'Add Broker'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
